export type NumericFeatureConfig = {
  defaultValue: number;
  max: number;
  min: number;
  step: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function numberFromConfig(
  config: unknown,
  key: string,
  fallback: number,
) {
  if (!isRecord(config)) {
    return fallback;
  }

  const value = config[key];
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

export function parseNumericConfig({
  config,
  defaultKeys,
  fallback,
}: {
  config: unknown;
  defaultKeys: string[];
  fallback: NumericFeatureConfig;
}): NumericFeatureConfig {
  const defaultValue = defaultKeys.reduce((resolved, key) => {
    if (resolved !== null) {
      return resolved;
    }

    const value = numberFromConfig(config, key, Number.NaN);
    return Number.isNaN(value) ? null : value;
  }, null as number | null);

  const min = numberFromConfig(config, "min", fallback.min);
  const max = numberFromConfig(config, "max", fallback.max);
  const step = numberFromConfig(config, "step", fallback.step);
  const safeMin = Math.min(min, max);
  const safeMax = Math.max(min, max);

  return {
    defaultValue: clampNumber(defaultValue ?? fallback.defaultValue, safeMin, safeMax),
    max: safeMax,
    min: safeMin,
    step: step > 0 ? step : fallback.step,
  };
}

export function clampNumber(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function normalizeSteppedValue(value: unknown, config: NumericFeatureConfig) {
  const numericValue =
    typeof value === "number" && Number.isFinite(value)
      ? value
      : config.defaultValue;
  const clamped = clampNumber(numericValue, config.min, config.max);
  const stepped =
    Math.round((clamped - config.min) / config.step) * config.step + config.min;
  return Number(clampNumber(stepped, config.min, config.max).toFixed(4));
}

export function formatNumber(value: number) {
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(2)));
}

export function createRangeControl({
  config,
  document,
  label,
  onPreferenceChange,
  preference,
  suffix = "",
}: {
  config: NumericFeatureConfig;
  document: Document;
  label: string;
  onPreferenceChange: (nextValue: unknown) => void;
  preference: unknown;
  suffix?: string;
}) {
  const wrapper = document.createElement("div");
  const labelElement = document.createElement("label");
  const controls = document.createElement("div");
  const decrementButton = document.createElement("button");
  const incrementButton = document.createElement("button");
  const valueElement = document.createElement("span");
  let value = normalizeSteppedValue(preference, config);

  wrapper.className = "migrosone-a11y-widget-feature-control";
  controls.className = "migrosone-a11y-widget-feature-stepper";
  labelElement.textContent = label;
  decrementButton.type = "button";
  decrementButton.className = "migrosone-a11y-widget-feature-stepper__button";
  decrementButton.textContent = "-";
  decrementButton.setAttribute("aria-label", `${label} azalt`);
  incrementButton.type = "button";
  incrementButton.className = "migrosone-a11y-widget-feature-stepper__button";
  incrementButton.textContent = "+";
  incrementButton.setAttribute("aria-label", `${label} artır`);
  valueElement.className = "migrosone-a11y-widget-feature-stepper__value";
  valueElement.setAttribute("aria-live", "polite");

  function sync(nextValue: number, shouldNotify: boolean) {
    value = normalizeSteppedValue(nextValue, config);
    valueElement.textContent = `${formatNumber(value)}${suffix}`;
    decrementButton.disabled = value <= config.min;
    incrementButton.disabled = value >= config.max;

    if (shouldNotify) {
      onPreferenceChange(value);
    }
  }

  decrementButton.addEventListener("click", () => {
    sync(value - config.step, true);
  });
  incrementButton.addEventListener("click", () => {
    sync(value + config.step, true);
  });

  sync(value, false);
  controls.append(decrementButton, valueElement, incrementButton);
  wrapper.append(labelElement, controls);
  return wrapper;
}
